import { useState } from 'react';
import { Card, EmptyState, ErrorMessage, Field, useAsync, useToast } from '../../components/ui';
import { RowActions } from '../../components/RowActions';
import { IconPlus } from '../../components/icons';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/** Feldnamen wie in der Antwort von GET /api/changelog – dort bewusst snake_case, siehe services/changelog.ts. */
interface ChangelogRow {
  id: number;
  version: string;
  release_date: string;
  section: string;
  item: string;
  sort_order: number;
}

/**
 * Änderungsverlauf verwalten (Kap. 17).
 *
 * Nur Superadmins: Der Verlauf beschreibt die Software als Ganzes, nicht
 * einen Mandanten – dieselbe Abgrenzung wie bei Datensicherung und
 * -wiederherstellung. Die Liste ist absichtlich flach (eine Zeile je Punkt,
 * nicht nach Version verschachtelt) – das macht Anlegen und Löschen
 * einzelner Punkte einfacher, auf Kosten einer etwas längeren Tabelle.
 */
export function ChangelogSettings() {
  const { user } = useAuth();
  const { notify } = useToast();
  const rows = useAsync(() => api.get('/api/changelog'), []);
  /** `id` gesetzt = bestehender Eintrag wird geändert, sonst neu angelegt. */
  const [draft, setDraft] = useState<{
    id?: number;
    version: string;
    releaseDate: string;
    section: string;
    item: string;
    sortOrder: number;
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (!user?.isSuperadmin) {
    return (
      <Card>
        <EmptyState title="Superadmins vorbehalten" hint="Der Änderungsverlauf beschreibt die Software als Ganzes, nicht einen einzelnen Teil davon." />
      </Card>
    );
  }

  const startDraft = () => {
    const latest = rows.data?.[0];
    setDraft({
      version: latest?.version ?? '',
      releaseDate: new Date().toISOString().slice(0, 10),
      section: 'Neu',
      item: '',
      // Ans Ende der laufenden Version stellen, statt wie bisher alles auf 0
      // zu lassen – sonst entscheidet bei gleicher Version und gleichem Datum
      // die Einfügereihenfolge über die Anzeige.
      sortOrder: Math.max(-1, ...(rows.data ?? [])
        .filter((row: ChangelogRow) => row.version === latest?.version)
        .map((row: ChangelogRow) => row.sort_order ?? 0)) + 1,
    });
  };

  const startEdit = (row: ChangelogRow) => {
    setError(null);
    setDraft({
      id: row.id,
      version: row.version,
      releaseDate: row.release_date,
      section: row.section ?? '',
      item: row.item,
      sortOrder: row.sort_order ?? 0,
    });
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const { id, ...payload } = draft;
      if (id) await api.patch(`/api/changelog/${id}`, payload);
      else await api.post('/api/changelog', payload);
      notify(id ? 'Eintrag geändert.' : 'Eintrag angelegt.', 'success');
      setDraft(null);
      rows.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <ErrorMessage error={error} />

      <Card
        title="Änderungsverlauf"
        subtitle="Erscheint in der Anwendung über die Versionsnummer (Fuß der Seitenleiste bzw. Profilseite)"
        actions={
          <button className="btn primary sm" onClick={startDraft}>
            <IconPlus width={14} height={14} /> Eintrag hinzufügen
          </button>
        }
        noBody
      >
        {rows.loading ? (
          <div className="card-body">Lädt …</div>
        ) : !rows.data?.length ? (
          <div className="card-body">
            <span className="small subtle">Noch kein Eintrag.</span>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Datum</th>
                  <th>Abschnitt</th>
                  <th>Text</th>
                  <th className="actions">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {rows.data.map((row: ChangelogRow) => (
                  <tr key={row.id}>
                    <td className="mono small">{row.version}</td>
                    <td className="small subtle">{row.release_date}</td>
                    <td className="small">{row.section || '–'}</td>
                    <td className="small">{row.item}</td>
                    <td className="actions">
                      <RowActions
                        label={`${row.version}: ${row.item}`}
                        onEdit={() => startEdit(row)}
                        deletePath={`/api/changelog/${row.id}`}
                        confirmText="Diesen Eintrag löschen?"
                        onDeleted={() => rows.reload()}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {draft && (
        <Card title={draft.id ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}>
          <div className="stack">
            <div className="form-grid">
              <Field label="Version" required hint="z. B. 0.3.0">
                <input value={draft.version} onChange={(event) => setDraft({ ...draft, version: event.target.value })} />
              </Field>
              <Field label="Datum" required>
                <input
                  type="date"
                  value={draft.releaseDate}
                  onChange={(event) => setDraft({ ...draft, releaseDate: event.target.value })}
                />
              </Field>
              <Field label="Abschnitt" hint="z. B. Neu, Behoben – leer lassen für keinen Abschnitt">
                <input value={draft.section} onChange={(event) => setDraft({ ...draft, section: event.target.value })} />
              </Field>
              <Field label="Reihenfolge" hint="Kleinere Zahl steht innerhalb der Version weiter oben.">
                <input
                  type="number"
                  value={draft.sortOrder}
                  onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) || 0 })}
                />
              </Field>
            </div>
            <Field label="Text" required>
              <input
                value={draft.item}
                autoFocus
                onChange={(event) => setDraft({ ...draft, item: event.target.value })}
                placeholder="Kurze, nutzerorientierte Beschreibung der Änderung"
              />
            </Field>
            <div className="row end">
              <button className="btn" onClick={() => setDraft(null)}>
                Abbrechen
              </button>
              <button className="btn primary" disabled={busy || !draft.version || !draft.item} onClick={save}>
                Speichern
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
